import "./Homepage.scss";

import {
  Box,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { IAPIConfig, useApi } from "../../custom_hooks/useApi";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  fetchAISearchData,
  fetchGA4Data,
  fetchLocalPresence,
  fetchSearchAnalytics,
} from "../../store/businessDashboardSlice";
import {
  fetchBusinessProfilesByUniqueIdSuccess,
  fetchBusinessProfilesFailure,
  resetBusinessProfiles,
  setExpandedBusiness,
  setProfileData,
} from "../../store/businessSlice";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import BusinessSidebar from "../../components/Sidebar/BusinessSidebar";
import CustomToast from "../../utils/cutomToast/CustomToast";
import HomeIcon from "@mui/icons-material/Home";
import IconButton from "@mui/material/IconButton";
import LogoutIcon from "@mui/icons-material/Logout";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import Sidebar from "../../components/Sidebar/AdminSidebar";
import SsidChartIcon from "@mui/icons-material/SsidChart";
import firebaseConfig from "../../firebaseConfig";
import { initializeApp } from "firebase/app";
import { logout } from "../../store/authSlice";
import { urlConstants } from "../../components/constants/globalConstants";
import { useBusinessProfileApis } from "../../custom_hooks/useBusinessProfileApis";
import { useConfirmDialog } from "../../components/Dialog/ConfirmDialogContext";

interface PushNotification {
  Title: string;
  Body: string;
  CreatedDate: string;
}

function SidebarContent() {
  const navigate = useNavigate();
  const { userDetails } = useSelector((state: any) => state.auth);
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState(""); // Manage search term
  const { doApi } = useApi();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [pushNotifications, setPushNotifications] = useState<
    PushNotification[]
  >([]);
  const { showConfirmDialog } = useConfirmDialog();
  const [notificationCount, setNotificationCount] = useState(0);
  const firebaseApp = initializeApp(firebaseConfig);
  const messaging = getMessaging(firebaseApp);
  const firstTime = useRef(true);
  const location = useLocation();
  const { businessData } = location.state || {};
  const { GetBusinessProfilesDataByUniqueID } = useBusinessProfileApis();

  // Handle search input change from child component
  const handleSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const searchRef = useRef<HTMLDivElement | null>(null);
  const [showSearch, setShowSearch] = useState<boolean>(false);

  // Toggle search input visibility
  const handleSearchToggle = () => {
    setShowSearch(!showSearch);
  };

  // Click outside logic to close the search input
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setShowSearch(false); // Close search input when clicking outside
      }
    }

    // Add event listener to detect outside clicks
    if (showSearch) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSearch]);

  const expandedBusiness = useSelector(
    (state: any) => state.businessDetails.expandedBusiness
  );

  useEffect(() => {
    if (businessData) {
      GetBusinessProfilesDataByUniqueID(businessData?.UniqueId);
    }
  }, [businessData]);

  const handleMenuClick = async (action: string) => {
    if (action === "logout") {
      const logoutConfig: IAPIConfig = {
        URL: "/logout",
        method: "POST",
      };
      try {
        const response = await doApi(logoutConfig, "seedmain");
        console.log(response);
      } catch (err) {
        console.log(err);
      } finally {
        dispatch(logout());
        dispatch(setExpandedBusiness(null));
        dispatch(resetBusinessProfiles());
        dispatch(fetchBusinessProfilesFailure({}));
        dispatch(fetchBusinessProfilesByUniqueIdSuccess({}));
        dispatch(fetchSearchAnalytics([]));
        dispatch(fetchGA4Data([]));
        dispatch(fetchLocalPresence([]));
        dispatch(fetchAISearchData([]));
        // dispatch(fetchWinnableData({}));
        sessionStorage.setItem("notificationDeniedToastShown", "false");
      }
    }
    setAnchorEl(null);
  };

  const saveTokenForPushNotification = async (token: string) => {
    const input = {
      pushToken: token,
    };
    const saveTokenConfig: IAPIConfig = {
      URL: "/save-token",
      method: "POST",
      payLoad: input,
    };

    try {
      const response = await doApi(saveTokenConfig, "seedmain");
      console.log(response);
    } catch (err) {
      console.log(err);
    }
  };

  const getAllPushNotifications = async () => {
    const pushNotificationsConfig: IAPIConfig = {
      URL: "/get-all-pushnotificatins",
      method: "GET",
    };

    try {
      const response: any = await doApi(pushNotificationsConfig, "seedmain");
      if (response.data.success) {
        setPushNotifications(response.data.data);
        setNotificationCount(response.data.data.length);
      } else {
        setPushNotifications([]);
        setNotificationCount(0);
      }
    } catch (err: any) {
      CustomToast(err, "error");
    }
  };

  useEffect(() => {
    const requestPermission = async () => {
      try {
        const permission = await Notification.requestPermission();
        if (permission === "granted") {
          console.log("Notification permission granted.");
          const token = await getToken(messaging, {
            vapidKey: urlConstants.VAPIDKEY,
          });
          if (token) {
            if (firstTime.current) {
              saveTokenForPushNotification(token);
              getAllPushNotifications();
              firstTime.current = false;
            }
          } else {
            console.log("No FCM token available.");
          }
        } else {
          const hasShownToast = sessionStorage.getItem(
            "notificationDeniedToastShown"
          );
          if (!hasShownToast) {
            CustomToast("Please Enable Notifications.", "error");
            sessionStorage.setItem("notificationDeniedToastShown", "true");
          }
        }
      } catch (error) {
        console.error("Error getting notification permission or token:", error);
      }
    };
    requestPermission();
    onMessage(messaging, (payload: any) => {
      console.log("Message received: ", payload);
      setPushNotifications((prevNotifications: any) => [
        {
          Title: payload.notification.title,
          Body: payload.notification.body,
          CreatedDate: new Date().toLocaleString(),
        },
        ...prevNotifications,
      ]);
      setNotificationCount((prevCount) => prevCount + 1);
      CustomToast(
        `${payload.notification.title}\n${payload.notification.body}`,
        "success"
      );
    });
  }, [messaging, doApi]);

  return (
    <Box className="homepage">
      <Drawer
        anchor="right"
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
      >
        <div style={{ width: 300 }}>
          <Typography variant="h6" style={{ padding: 16 }}>
            Notifications
          </Typography>
          <List>
            {pushNotifications.map((item, index) => (
              <ListItem key={index}>
                <ListItemText
                  primary={item.Title}
                  secondary={
                    <div>
                      <div>{item.Body}</div>
                      <div style={{ color: "gray", fontSize: "12px" }}>
                        {new Date(item.CreatedDate).toLocaleString()}
                      </div>
                    </div>
                  }
                />
              </ListItem>
            ))}
          </List>
        </div>
      </Drawer>

      <Box className="sidebar-drawer" width={"100%"}>
        <Box className="sidebar-header" width={"100%"}>
          <Box className="sidebar-title">
            <Box className="title" padding={2}>
              <Typography variant="h5" fontWeight={600} sx={{ color: "white" }}>
                massic
              </Typography>
            </Box>
            <Box width={"100%"}>
              {userDetails.roleid !== 2 && (
                <Button
                  color="primary"
                  size="medium"
                  fullWidth
                  variant="contained"
                  className="sidebar-action-button"
                  startIcon={<HomeIcon />}
                  onClick={() => {
                    navigate("/home");
                    dispatch(setExpandedBusiness(null));
                    dispatch(setProfileData([]));
                  }}
                >
                  Home
                </Button>
              )}
              {false && (
                <Button
                  color="primary"
                  size="medium"
                  fullWidth
                  variant="contained"
                  className="sidebar-action-button"
                  startIcon={<SsidChartIcon />}
                  onClick={() => {
                    navigate("/home");
                    dispatch(setExpandedBusiness(null));
                    dispatch(setProfileData([]));
                  }}
                >
                  Proposals
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        <Box
          className="sidebar-body"
          marginTop={2}
          marginBottom={1}
          width={"100%"}
        >
          <Sidebar />

          {/* Search input field (conditionally rendered) */}
          {userDetails.roleid !== 2 &&
            (showSearch ? (
              <Box paddingX={2} paddingY={1.3} ref={searchRef}>
                <TextField
                  placeholder="Enter Business Name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  className="custom-search-input"
                  onChange={handleSearchInput}
                  value={searchTerm}
                />
              </Box>
            ) : (
              <Box
                className="sidebar-body-label"
                width={"100%"}
                marginBottom={0.5}
                display={"flex"}
                alignItems={"center"}
                justifyContent={"space-between"}
                padding={2}
              >
                <Typography
                  variant="overline"
                  className="sidebar-body-label-name"
                  display={"flex"}
                  alignItems={"center"}
                  lineHeight={0}
                  color="rgba(255, 255, 255, 0.38)"
                  letterSpacing={1.5}
                >
                  BUSINESSES
                </Typography>
                <Box display={"flex"} alignItems={"center"} gap={2.5}>
                  <IconButton
                    aria-label="delete"
                    sx={{
                      color: "white",
                      width: "14px",
                      height: "14px",
                    }}
                    onClick={() => navigate("/authentication-after-login")}
                  >
                    <AddCircleOutlineIcon />
                  </IconButton>
                  <IconButton
                    aria-label="search"
                    sx={{ color: "white", width: "14px", height: "14px" }}
                    onClick={handleSearchToggle}
                  >
                    <SearchIcon />
                  </IconButton>
                </Box>
              </Box>
            ))}
          <Box
            className="business-sidebar"
            flex={1}
            overflow={"auto"}
            maxHeight={"calc(100vh - 370px)"}
            minHeight={"calc(100vh - 370px)"}
            width={"100%"}
          >
            <BusinessSidebar
              expandedBusiness={expandedBusiness}
              setExpandedBusiness={setExpandedBusiness}
              searchTerm={searchTerm}
            />
          </Box>
        </Box>

        <Box className="sidebar-footer" width={"100%"}>
          <Box className="line"></Box>
          <Box
            className=""
            paddingX={2}
            paddingY={0.4}
            display={"flex"}
            alignItems={"center"}
            marginTop={1}
            gap={1}
          >
            <Typography
              variant="overline"
              className="ellipsis"
              color="white"
              letterSpacing={1}
              maxWidth={"50%"}
            >
              {userDetails.name}
            </Typography>

            <Box
              width={"5px"}
              height={"5px"}
              borderRadius={"50%"}
              bgcolor={"rgba(255, 255, 255, 0.38)"}
            ></Box>

            <Typography
              variant="overline"
              className=""
              color="rgba(255, 255, 255, 0.38)"
              letterSpacing={1}
            >
              {userDetails.organzationName}
            </Typography>
          </Box>
          <Box>
            <Button
              variant="contained"
              size="medium"
              fullWidth
              color="primary"
              className="sidebar-action-button"
              startIcon={<SettingsIcon />}
              onClick={() => {
                navigate("/settings");
              }}
            >
              Settings
            </Button>
            <Button
              variant="contained"
              size="medium"
              fullWidth
              color="primary"
              className="sidebar-action-button"
              startIcon={<NotificationsIcon />}
              onClick={() => setDrawerVisible(true)}
            >
              Notifications
            </Button>

            <Button
              variant="contained"
              size="medium"
              fullWidth
              color="primary"
              className="sidebar-action-button"
              sx={{
                color: "#338484 !important",
              }}
              startIcon={<LogoutIcon />}
              onClick={() =>{
                debugger;
                showConfirmDialog({
                  title: "Confirmation",
                  message: `Are you sure want to logout ?`,
                  confirmText: "Logout",
                  cancelText: "Cancel",
                  onConfirm: () => {
                    handleMenuClick("logout");
                  },
                  onCancel: () => {
                    console.log("Trigger action cancelled!");
                  },
                })
              }}
            >
              Logout
            </Button>
          </Box>
          <Box paddingX={2} paddingY={1} display={"flex"} alignItems={"center"}>
            <Typography
              variant="caption"
              className="sidebar-footer-version"
              color="rgba(255, 255, 255, 0.38)"
            >
              {urlConstants.MASSIC_VERSION}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

const AdminHomepage = () => {
  // const [expandedBusiness, setExpandedBusiness] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  // Function to toggle sidebar visibility
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const cssClass = useMemo(
    () => (location.pathname === "/analytics" ? "analyticsbg" : "remaingbg"),
    [location.pathname]
  );
  return (
    <Box className={`homepage ${cssClass}`}>
      <Box>
        {/* Sidebar for larger screens */}
        <Box
          sx={{
            display: { xs: "none !important", md: "flex !important" },
            position: "relative",
            flexDirection: "column",
            width: "15rem",
            height: "100%",
            backgroundColor: "var(--massic-900, #042525)",
          }}
        >
          <SidebarContent /> {/* Reuse the SidebarContent component here */}
        </Box>
        {/* Drawer for mobile/tablet screens */}
        <Drawer
          anchor="left"
          open={isSidebarOpen}
          onClose={toggleSidebar}
          sx={{
            display: { xs: "block", md: "none" }, // Show on mobile/tablet
            "& .MuiDrawer-paper": {
              width: "15rem",
              backgroundColor: "var(--massic-900, #042525)",
            },
            height: "100vh",
          }}
        >
          <SidebarContent />
        </Drawer>
      </Box>

      <Box className="container-body">
        {/* <Navbar toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} /> */}
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdminHomepage;
